import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Breadcrumbs, Card, CardContent } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  DirectionsCar as DirectionsCarIcon,
  EventNote as EventNoteIcon,
  Login as LoginIcon,
  VpnKey as VpnKeyIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import Button from '../../../components/Button';
import ExportButton from '../../../components/ExportButton';
import DateRangePicker from '../../../components/DateRangePicker';
import { useNotification } from '../../../components/Notification';
import Select from '../../../components/Select';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import {
  fetchInit,
  fetchAccessReport,
  exportAccessReportToExcel,
  exportAccessReportToPdf,
  selectAccessReportData,
  selectAccessReportUnits,
  selectAccessReportLoading,
  selectAccessReportExportLoading,
  selectAccessReportPdfExportLoading,
  selectAccessReportError,
  selectAccessReportSuccessMessage,
  selectAccessReportFilterUnitId,
  selectAccessReportFilterFromDate,
  selectAccessReportFilterToDate,
  setFilterUnitId,
  setFilterFromDate,
  setFilterToDate,
  clearFilters,
  clearError,
  clearSuccessMessage,
} from './slice';

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function AccessReport() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const reportData = useSelector(selectAccessReportData);
  const units = useSelector(selectAccessReportUnits);
  const loading = useSelector(selectAccessReportLoading);
  const exportLoading = useSelector(selectAccessReportExportLoading);
  const pdfExportLoading = useSelector(selectAccessReportPdfExportLoading);
  const error = useSelector(selectAccessReportError);
  const successMessage = useSelector(selectAccessReportSuccessMessage);
  const filterUnitId = useSelector(selectAccessReportFilterUnitId);
  const filterFromDate = useSelector(selectAccessReportFilterFromDate);
  const filterToDate = useSelector(selectAccessReportFilterToDate);

  // Local state for new filters
  const [filterObjectType, setFilterObjectType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  // Object types options
  const objectTypeOptions = useMemo(() => [
    { id: '', name: t('reports.access.filters.allObjectTypes') },
    { id: 'employee', name: t('reports.access.filters.objectTypes.employee') },
    { id: 'visitor', name: t('reports.access.filters.objectTypes.visitor') },
    { id: 'vehicle', name: t('reports.access.filters.objectTypes.vehicle') },
    { id: 'contractor', name: t('reports.access.filters.objectTypes.contractor') },
  ], [t]);

  // Location options
  const locationOptions = useMemo(() => [
    { id: '', name: t('reports.access.filters.allLocations') },
    { id: 'main_gate', name: t('reports.access.filters.locations.mainGate') },
    { id: 'back_gate', name: t('reports.access.filters.locations.backGate') },
    { id: 'parking_lot', name: t('reports.access.filters.locations.parkingLot') },
    { id: 'building_a', name: t('reports.access.filters.locations.buildingA') },
    { id: 'building_b', name: t('reports.access.filters.locations.buildingB') },
    { id: 'warehouse', name: t('reports.access.filters.locations.warehouse') },
  ], [t]);

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Helper to check if value is empty (null, undefined, or empty string - NOT 0)
  const isEmpty = (value) => value === '' || value === null || value === undefined;

  // Auto-select first unit and today's date after init completes
  useEffect(() => {
    if (units.length > 0) {
      // Set first unit if not set
      if (isEmpty(filterUnitId)) {
        dispatch(setFilterUnitId(units[0].id));
      }
      // Set today's date for both from and to if not set
      const today = getTodayDate();
      if (isEmpty(filterFromDate)) {
        dispatch(setFilterFromDate(today));
      }
      if (isEmpty(filterToDate)) {
        dispatch(setFilterToDate(today));
      }
    }
  }, [units, filterUnitId, filterFromDate, filterToDate, dispatch]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(t(error), 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification, t]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch, showNotification, t]);

  const handleSearch = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.access.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(fetchAccessReport());
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setFilterObjectType('');
    setFilterLocation('');
  };

  const handleExportExcel = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.access.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportAccessReportToExcel());
  };

  const handleExportPdf = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.access.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportAccessReportToPdf());
  };

  // Define dashboard statistics data grouped by rows - using reportData from API
  const dashboardStats = reportData
    ? {
      // Row 1: Access Events (2 boxes)
      row1: [
        {
          key: 'totalAccessEvents',
          label: t('reports.access.dashboard.totalAccessEvents'),
          value: reportData.totalAccessEvents || 0,
          icon: <EventNoteIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#1976d2',
          bgColor: '#e3f2fd',
        },
        {
          key: 'totalAbnormalEvents',
          label: t('reports.access.dashboard.totalAbnormalEvents'),
          value: reportData.totalAbnormalEvents || 0,
          icon: <WarningIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#d32f2f',
          bgColor: '#ffebee',
        },
      ],
      // Row 2: Users and Visits (4 boxes)
      row2: [
        {
          key: 'totalUsers',
          label: t('reports.access.dashboard.totalUsers'),
          value: reportData.totalUsers || 0,
          icon: <PersonIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#388e3c',
          bgColor: '#e8f5e9',
        },
        {
          key: 'totalUsersAccess',
          label: t('reports.access.dashboard.totalUsersAccess'),
          value: reportData.totalUsersAccess || 0,
          icon: <PeopleIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#f57c00',
          bgColor: '#fff3e0',
        },
        {
          key: 'totalVisits',
          label: t('reports.access.dashboard.totalVisits'),
          value: reportData.totalVisits || 0,
          icon: <GroupIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#7b1fa2',
          bgColor: '#f3e5f5',
        },
        {
          key: 'totalVisitsAccess',
          label: t('reports.access.dashboard.totalVisitsAccess'),
          value: reportData.totalVisitsAccess || 0,
          icon: <VpnKeyIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#c2185b',
          bgColor: '#fce4ec',
        },
      ],
      // Row 3: Vehicle Users and Visits (4 boxes)
      row3: [
        {
          key: 'totalVehicleUsers',
          label: t('reports.access.dashboard.totalVehicleUsers'),
          value: reportData.totalVehicleUsers || 0,
          icon: <DirectionsCarIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#00796b',
          bgColor: '#e0f2f1',
        },
        {
          key: 'totalVehicleUsersAccess',
          label: t('reports.access.dashboard.totalVehicleUsersAccess'),
          value: reportData.totalVehicleUsersAccess || 0,
          icon: <LoginIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#0288d1',
          bgColor: '#e1f5fe',
        },
        {
          key: 'totalVehicleVisits',
          label: t('reports.access.dashboard.totalVehicleVisits'),
          value: reportData.totalVehicleVisits || 0,
          icon: <DirectionsCarIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#5d4037',
          bgColor: '#efebe9',
        },
        {
          key: 'totalVehicleVisitsAccess',
          label: t('reports.access.dashboard.totalVehicleVisitsAccess'),
          value: reportData.totalVehicleVisitsAccess || 0,
          icon: <DirectionsCarIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#455a64',
          bgColor: '#eceff1',
        },
      ],
    }
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="body2">{t('sidebar.module6.title')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('sidebar.module6.accessReport')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left', mb: 3 }}>
          {t('reports.access.title')}
        </Typography>

        {/* Filter Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Select
              label={t('reports.access.filters.unit')}
              value={filterUnitId}
              onChange={(e) => dispatch(setFilterUnitId(e.target.value))}
              options={[{ id: '', name: t('reports.filters.all') }, ...units.map(u => ({ id: u.id, name: u.name }))]}
              id="filterUnitId"
              name="filterUnitId"
              data-testid="accessReport-select-filterUnitId"
              fullWidth={true}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Select
              label={t('reports.access.filters.objectType')}
              value={filterObjectType}
              onChange={(e) => setFilterObjectType(e.target.value)}
              options={objectTypeOptions}
              id="filterObjectType"
              name="filterObjectType"
              data-testid="accessReport-select-filterObjectType"
              fullWidth={true}
            />
          </Box>
          <Box sx={{ minWidth: 300 }}>
            <DateRangePicker
              label={t('reports.access.filters.dateRange')}
              fromValue={filterFromDate}
              toValue={filterToDate}
              onFromChange={(e) => dispatch(setFilterFromDate(e.target.value))}
              onToChange={(e) => dispatch(setFilterToDate(e.target.value))}
              id="filterDateRange"
              name="filterDateRange"
              data-testid="accessReport-daterangepicker-filter"
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Select
              label={t('reports.access.filters.location')}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              options={locationOptions}
              id="filterLocation"
              name="filterLocation"
              data-testid="accessReport-select-filterLocation"
              fullWidth={true}
            />
          </Box>
          <Button
            variant="primary"
            icon="search"
            iconPosition="left"
            onClick={handleSearch}
            loading={loading}
            id="searchButton"
            name="searchButton"
            data-testid="accessReport-button-search"
            size="lg"
          >
            {t('reports.access.search')}
          </Button>
          <Button
            variant="outline"
            icon="close"
            iconPosition="left"
            onClick={handleClearFilters}
            disabled={loading}
            id="clearFilters"
            name="clearFilters"
            data-testid="accessReport-button-clearFilters"
            size="lg"
          >
            {t('reports.filters.clear')}
          </Button>
          <ExportButton
            variant="outline"
            onExportExcel={handleExportExcel}
            onExportPdf={handleExportPdf}
            loading={exportLoading || pdfExportLoading}
            disabled={loading}
            id="exportButton"
            name="exportButton"
            data-testid="accessReport-button-export"
            size="lg"
          />
        </Box>
      </Box>

      {/* Dashboard Section - Show when API returns data */}
      {dashboardStats && (
        <Box sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5', flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('reports.access.dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(() => {
              const objectTypeName = filterObjectType ? objectTypeOptions.find(o => o.id === filterObjectType)?.name : t('reports.access.filters.allObjectTypes');
              const locationName = filterLocation ? locationOptions.find(l => l.id === filterLocation)?.name : t('reports.access.filters.allLocations');
              const formattedFromDate = filterFromDate ? formatDateWithCulture(filterFromDate, i18n.language) : '---';
              const formattedToDate = filterToDate ? formatDateWithCulture(filterToDate, i18n.language) : '---';
              return `${objectTypeName} | ${locationName} | ${formattedFromDate} - ${formattedToDate}`;
            })()}
          </Typography>

          {/* Row 1: Access Events (2 boxes at 50% each) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {dashboardStats.row1.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 calc(50% - 8px)' },
                  minWidth: { xs: '100%', sm: '250px' }
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Row 2: Users and Visits (4 boxes at 25% each) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {dashboardStats.row2.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
                  minWidth: { xs: '100%', sm: '200px' }
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Row 3: Vehicle Users and Visits (4 boxes at 25% each) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {dashboardStats.row3.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' },
                  minWidth: { xs: '100%', sm: '200px' }
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      )}

    </Box>
  );
}

export default AccessReport;
